import { useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  FUNCTION_KEYS,
  FUNCTION_LABELS,
  FORMAT_RULES,
  type MappingCreate,
  type MappingResponse,
  type ColumnInfo,
} from '@flixflow/shared';
import { importProfilesApi } from '../api/importProfiles.js';
import { importRunsApi } from '../api/importRuns.js';
import { schemaApi } from '../api/schema.js';
import { getCsrfToken } from '../api/client.js';

// ── Mapping Row ───────────────────────────────────────────────────────────────

function MappingRow({
  mapping,
  profileId,
  position,
  total,
}: {
  mapping: MappingResponse;
  profileId: string;
  position: number;
  total: number;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const { register, handleSubmit, watch, reset } = useForm<MappingCreate>({
    defaultValues: {
      position: mapping.position,
      targetField: mapping.targetField,
      sourceField: mapping.sourceField ?? undefined,
      staticValue: mapping.staticValue ?? undefined,
      functionKey: (mapping.functionKey as MappingCreate['functionKey']) ?? undefined,
      maxLength: mapping.maxLength ?? undefined,
      formatRule: (mapping.formatRule as MappingCreate['formatRule']) ?? undefined,
      emptyTransfers: mapping.emptyTransfers,
      required: mapping.required,
      hint: mapping.hint ?? undefined,
    },
  });

  const functionKey = watch('functionKey');

  const updateMut = useMutation({
    mutationFn: (data: MappingCreate) =>
      importProfilesApi.updateMapping(profileId, mapping.id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['import-profile', profileId] });
      setEditing(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => importProfilesApi.deleteMapping(profileId, mapping.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['import-profile', profileId] }),
  });

  const moveMut = useMutation({
    mutationFn: async (dir: 'up' | 'down') => {
      const profile = await importProfilesApi.get(profileId);
      const mappings = profile.mappings ?? [];
      const sorted = [...mappings].sort((a, b) => a.position - b.position);
      const idx = sorted.findIndex((m) => m.id === mapping.id);
      if (dir === 'up' && idx > 0) [sorted[idx - 1], sorted[idx]] = [sorted[idx]!, sorted[idx - 1]!];
      if (dir === 'down' && idx < sorted.length - 1)
        [sorted[idx + 1], sorted[idx]] = [sorted[idx]!, sorted[idx + 1]!];
      return importProfilesApi.reorderMappings(
        profileId,
        sorted.map((m) => m.id),
      );
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['import-profile', profileId] }),
  });

  if (editing) {
    return (
      <tr className="bg-blue-50">
        <td colSpan={7} className="px-4 py-3">
          <form
            onSubmit={handleSubmit((d) => updateMut.mutate(d))}
            className="space-y-2"
          >
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-neutral-600">Zielfeld</label>
                <input
                  {...register('targetField')}
                  className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-600">Quellfeld (Header)</label>
                <input
                  {...register('sourceField')}
                  className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  placeholder="optional"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-600">Funktion</label>
                <select {...register('functionKey')} className="w-full border rounded px-2 py-1 text-sm mt-0.5">
                  <option value="">-- keine --</option>
                  {FUNCTION_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {FUNCTION_LABELS[k]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(!functionKey || functionKey === 'staticIfEmpty') && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-neutral-600">Statischer Wert</label>
                  <input
                    {...register('staticValue')}
                    className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-600">MaxLänge</label>
                  <input
                    type="number"
                    {...register('maxLength', { valueAsNumber: true })}
                    className="w-full border rounded px-2 py-1 text-sm mt-0.5"
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-600">Formatierung</label>
                  <select {...register('formatRule')} className="w-full border rounded px-2 py-1 text-sm mt-0.5">
                    <option value="">-- keine --</option>
                    {FORMAT_RULES.map((r) => (
                      <option key={r} value={r}>
                        {r === 'DATE_DE'
                          ? 'Datum TT.MM.JJJJ'
                          : r === 'BOOL_10'
                            ? 'Wahr/Falsch → 1/0'
                            : 'Wahr/Falsch → 0/1 (negiert)'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="flex gap-4 text-xs">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" {...register('required')} /> Pflichtfeld
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" {...register('emptyTransfers')} /> Leere Werte übertragen
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={updateMut.isPending}
                className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
              >
                Speichern
              </button>
              <button
                type="button"
                onClick={() => {
                  reset();
                  setEditing(false);
                }}
                className="px-3 py-1 border text-xs rounded hover:bg-neutral-50"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-4 py-2 text-center text-neutral-400 w-20">
        <div className="flex gap-1 justify-center">
          <button
            disabled={position === 0}
            onClick={() => moveMut.mutate('up')}
            className="text-neutral-400 hover:text-neutral-700 disabled:opacity-30 text-xs"
            title="Nach oben"
          >
            ▲
          </button>
          <button
            disabled={position === total - 1}
            onClick={() => moveMut.mutate('down')}
            className="text-neutral-400 hover:text-neutral-700 disabled:opacity-30 text-xs"
            title="Nach unten"
          >
            ▼
          </button>
        </div>
      </td>
      <td className="px-4 py-2 font-mono text-xs font-semibold">{mapping.targetField}</td>
      <td className="px-4 py-2 text-xs text-neutral-600">
        {mapping.sourceField ?? <span className="text-neutral-300">—</span>}
      </td>
      <td className="px-4 py-2 text-xs text-neutral-600">
        {mapping.functionKey ? (
          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">
            {FUNCTION_LABELS[mapping.functionKey as keyof typeof FUNCTION_LABELS] ?? mapping.functionKey}
          </span>
        ) : mapping.staticValue ? (
          <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs">
            "{mapping.staticValue}"
          </span>
        ) : (
          <span className="text-neutral-300">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-neutral-500">
        {mapping.formatRule ?? <span className="text-neutral-300">—</span>}
      </td>
      <td className="px-4 py-2 text-xs">
        {mapping.required && (
          <span className="text-red-500 font-medium mr-1">P</span>
        )}
        {mapping.emptyTransfers && (
          <span className="text-neutral-400 text-xs">∅</span>
        )}
      </td>
      <td className="px-4 py-2 text-right text-xs space-x-3">
        <button onClick={() => setEditing(true)} className="text-blue-600 hover:underline">
          Bearbeiten
        </button>
        <button
          onClick={() => {
            if (confirm(`Mapping "${mapping.targetField}" löschen?`)) deleteMut.mutate();
          }}
          className="text-red-500 hover:text-red-700"
        >
          Löschen
        </button>
      </td>
    </tr>
  );
}

// ── Add Mapping Form ──────────────────────────────────────────────────────────

function AddMappingForm({
  profileId,
  columns,
  nextPosition,
}: {
  profileId: string;
  columns: ColumnInfo[];
  nextPosition: number;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { register, handleSubmit, watch, reset } = useForm<MappingCreate>({
    defaultValues: { position: nextPosition, emptyTransfers: false, required: false },
  });
  const functionKey = watch('functionKey');

  const addMut = useMutation({
    mutationFn: (data: MappingCreate) => importProfilesApi.createMapping(profileId, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['import-profile', profileId] });
      reset({ position: nextPosition + 1, emptyTransfers: false, required: false });
      setOpen(false);
    },
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-4 px-4 py-2 border-2 border-dashed border-neutral-300 text-sm text-neutral-500 rounded-lg w-full hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        + Mapping hinzufügen
      </button>
    );
  }

  return (
    <div className="mt-4 border rounded-lg p-4 bg-neutral-50">
      <h4 className="text-sm font-medium mb-3">Neues Mapping</h4>
      <form onSubmit={handleSubmit((d) => addMut.mutate(d))} className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-neutral-600">Zielfeld (MSSQL-Spalte)</label>
            {columns.length > 0 ? (
              <select {...register('targetField')} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5">
                <option value="">-- wählen --</option>
                {columns.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name} ({c.dataType})
                  </option>
                ))}
              </select>
            ) : (
              <input
                {...register('targetField')}
                className="w-full border rounded px-2 py-1.5 text-sm mt-0.5"
                placeholder="A_nr"
              />
            )}
          </div>
          <div>
            <label className="text-xs text-neutral-600">Quellfeld (CSV/Excel-Header)</label>
            <input
              {...register('sourceField')}
              className="w-full border rounded px-2 py-1.5 text-sm mt-0.5"
              placeholder="Artikelnummer"
            />
          </div>
          <div>
            <label className="text-xs text-neutral-600">Funktion</label>
            <select {...register('functionKey')} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5">
              <option value="">-- keine --</option>
              {FUNCTION_KEYS.map((k) => (
                <option key={k} value={k}>
                  {FUNCTION_LABELS[k]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {(!functionKey || functionKey === 'staticIfEmpty') && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-neutral-600">Statischer Wert</label>
              <input
                {...register('staticValue')}
                className="w-full border rounded px-2 py-1.5 text-sm mt-0.5"
              />
            </div>
            <div>
              <label className="text-xs text-neutral-600">MaxLänge</label>
              <input
                type="number"
                {...register('maxLength', { valueAsNumber: true })}
                className="w-full border rounded px-2 py-1.5 text-sm mt-0.5"
              />
            </div>
            <div>
              <label className="text-xs text-neutral-600">Formatierung</label>
              <select {...register('formatRule')} className="w-full border rounded px-2 py-1.5 text-sm mt-0.5">
                <option value="">-- keine --</option>
                {FORMAT_RULES.map((r) => (
                  <option key={r} value={r}>
                    {r === 'DATE_DE'
                      ? 'Datum TT.MM.JJJJ'
                      : r === 'BOOL_10'
                        ? 'Wahr/Falsch → 1/0'
                        : 'Wahr/Falsch → 0/1 (negiert)'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex gap-4 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" {...register('required')} /> Pflichtfeld
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" {...register('emptyTransfers')} /> Leere Werte übertragen
          </label>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={addMut.isPending}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Hinzufügen
          </button>
          <button
            type="button"
            onClick={() => { reset(); setOpen(false); }}
            className="px-4 py-1.5 border text-sm rounded hover:bg-neutral-100"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Import Trigger ────────────────────────────────────────────────────────────

function ImportTrigger({ profileId }: { profileId: string }) {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = getCsrfToken();
    if (!token) { setError('Kein CSRF-Token'); return; }

    setUploading(true);
    setError(null);

    try {
      const run = await importRunsApi.trigger(profileId, file, token);
      void navigate(`/import-run/${run.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mt-8 rounded-lg border border-dashed border-neutral-300 p-6 text-center">
      <p className="text-sm text-neutral-600 mb-3">
        Datei hochladen und Import starten
      </p>
      <label className={`inline-block px-5 py-2 rounded text-sm font-medium cursor-pointer transition-colors ${
        uploading
          ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
          : 'bg-green-600 text-white hover:bg-green-700'
      }`}>
        {uploading ? 'Wird hochgeladen…' : 'Datei wählen & Import starten'}
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,.csv"
          className="hidden"
          disabled={uploading}
          onChange={(e) => void handleUpload(e)}
        />
      </label>
      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ImportProfileDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['import-profile', id],
    queryFn: () => importProfilesApi.get(id!),
    enabled: !!id,
  });

  const { data: columns = [] } = useQuery({
    queryKey: ['schema-columns', profile?.connectionId, profile?.targetTable],
    queryFn: () => schemaApi.listColumns(profile!.connectionId, profile!.targetTable),
    enabled: !!profile?.connectionId && !!profile?.targetTable,
    staleTime: 60_000,
  });

  const { data: runs = [] } = useQuery({
    queryKey: ['import-runs', id],
    queryFn: () => importRunsApi.listForProfile(id!, 10),
    enabled: !!id,
    refetchInterval: 5000,
  });

  if (isLoading) return <div className="mx-auto max-w-6xl px-6 py-10 text-neutral-500">Lade…</div>;
  if (!profile) return <div className="mx-auto max-w-6xl px-6 py-10">Profil nicht gefunden.</div>;

  const mappings = (profile.mappings ?? []).sort((a, b) => a.position - b.position);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Breadcrumb */}
      <div className="text-sm text-neutral-500 mb-4">
        <Link to="/importprofile" className="hover:underline">Importprofile</Link>
        {' › '}
        <span className="text-neutral-800 font-medium">{profile.name}</span>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">{profile.name}</h2>
          <p className="text-sm text-neutral-500 mt-1">
            {profile.bereich} → <code className="font-mono text-xs bg-neutral-100 px-1 rounded">{profile.targetTable}</code>
            {' · '}
            {profile.sourceFormat === 'EXCEL' ? 'Excel' : 'CSV'}
            {profile.sheetName && ` (Sheet: ${profile.sheetName})`}
            {' · '}
            {profile.onlyChanged ? 'Nur geänderte' : 'Alle Datensätze'}
          </p>
        </div>
      </div>

      {/* Mappings */}
      <div className="rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            Feld-Mappings ({mappings.length})
          </h3>
          {columns.length > 0 && (
            <span className="text-xs text-neutral-400">
              {columns.length} Spalten aus MSSQL geladen
            </span>
          )}
        </div>

        {mappings.length === 0 ? (
          <div className="px-4 py-8 text-center text-neutral-400 text-sm">
            Noch keine Mappings. Füge unten Felder hinzu.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-neutral-500 bg-neutral-50 border-b border-neutral-100">
              <tr>
                <th className="px-4 py-2 text-center w-20">Reihenfolge</th>
                <th className="px-4 py-2 text-left">Zielfeld</th>
                <th className="px-4 py-2 text-left">Quellfeld</th>
                <th className="px-4 py-2 text-left">Funktion / Statisch</th>
                <th className="px-4 py-2 text-left">Format</th>
                <th className="px-4 py-2 text-left">Flags</th>
                <th className="px-4 py-2 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {mappings.map((m, i) => (
                <MappingRow
                  key={m.id}
                  mapping={m}
                  profileId={profile.id}
                  position={i}
                  total={mappings.length}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddMappingForm
        profileId={profile.id}
        columns={columns}
        nextPosition={mappings.length}
      />

      <ImportTrigger profileId={profile.id} />

      {/* Recent runs */}
      {runs.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold mb-3">Letzte Import-Läufe</h3>
          <div className="rounded-lg border border-neutral-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500">
                <tr>
                  <th className="px-4 py-2 text-left">Gestartet</th>
                  <th className="px-4 py-2 text-left">Datei</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Zeilen ok / skip / Fehler</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {runs.map((r) => (
                  <tr key={r.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-2 text-xs text-neutral-500">
                      {new Date(r.startedAt).toLocaleString('de-DE')}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono truncate max-w-xs">
                      {r.sourceFileName}
                    </td>
                    <td className="px-4 py-2">
                      <RunStateBadge state={r.state} />
                    </td>
                    <td className="px-4 py-2 text-xs text-right text-neutral-600">
                      {r.rowsOk} / {r.rowsSkipped} / {r.rowsError}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Link
                        to={`/import-run/${r.id}`}
                        className="text-blue-600 text-xs hover:underline"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RunStateBadge({ state }: { state: string }) {
  const styles: Record<string, string> = {
    QUEUED: 'bg-neutral-100 text-neutral-600',
    RUNNING: 'bg-blue-100 text-blue-700 animate-pulse',
    DONE: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-orange-100 text-orange-700',
    INTERRUPTED: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${styles[state] ?? 'bg-neutral-100 text-neutral-600'}`}>
      {state}
    </span>
  );
}
